import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  CircularProgress,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
  FilterList,
  Cloud,
  Storage,
  NetworkCheck,
  Computer,
  Memory,
  Storage as StorageIcon,
  NetworkCheck as NetworkIcon,
} from '@mui/icons-material';
import { inventoryAPI } from '../api';
import debounce from 'lodash.debounce';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// VM Grid Component
const VMGrid = ({ vms, onVMClick }) => {
  const [selectedVM, setSelectedVM] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleVMClick = (vm) => {
    setSelectedVM(vm);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedVM(null);
  };

  const getVMStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'running':
        return 'success';
      case 'stopped':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getVMTypeIcon = (instanceType) => {
    if (instanceType?.includes('micro') || instanceType?.includes('small')) {
      return <Computer sx={{ fontSize: 24, color: '#4CAF50' }} />;
    } else if (instanceType?.includes('large') || instanceType?.includes('xlarge')) {
      return <Computer sx={{ fontSize: 24, color: '#FF9800' }} />;
    } else {
      return <Computer sx={{ fontSize: 24, color: '#2196F3' }} />;
    }
  };

  const formatTags = (tags) => {
    if (!tags || typeof tags === 'string') {
      try {
        tags = JSON.parse(tags);
      } catch {
        return {};
      }
    }
    // Ensure we always return an object, not null or undefined
    return tags && typeof tags === 'object' ? tags : {};
  };

  return (
    <>
      <Grid container spacing={2}>
        {vms.map((vm) => {
          const tags = formatTags(vm.tags);
          const status = vm.status || 'unknown';
          
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={vm.resource_id}>
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2">
                      <strong>ID:</strong> {vm.resource_id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Type:</strong> {vm.instance_type || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Region:</strong> {vm.region}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Status:</strong> {status}
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
              >
                <Card
                  sx={{
                    height: 200,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  onClick={() => handleVMClick(vm)}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                      {getVMTypeIcon(vm.instance_type)}
                    </Box>
                    
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {vm.name || vm.resource_id}
                    </Typography>
                    
                    <Chip
                      label={status}
                      color={getVMStatusColor(status)}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {vm.instance_type || 'N/A'}
                    </Typography>
                    
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {vm.region}
                    </Typography>
                  </CardContent>
                </Card>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>

      {/* VM Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedVM && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getVMTypeIcon(selectedVM.instance_type)}
                <Typography variant="h6">
                  {selectedVM.name || selectedVM.resource_id}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Basic Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Resource ID
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace">
                      {selectedVM.resource_id}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Instance Type
                    </Typography>
                    <Typography variant="body1">
                      {selectedVM.instance_type || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Region
                    </Typography>
                    <Typography variant="body1">
                      {selectedVM.region}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={selectedVM.status || 'unknown'}
                      color={getVMStatusColor(selectedVM.status)}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Tags
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {(() => {
                      const formattedTags = formatTags(selectedVM.tags);
                      return formattedTags && Object.keys(formattedTags).length > 0 ? (
                        Object.entries(formattedTags).map(([key, value]) => (
                          <Chip
                            key={key}
                            label={`${key}: ${value}`}
                            size="small"
                            sx={{ m: 0.5 }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No tags available
                        </Typography>
                      );
                    })()}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Additional Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated: {new Date(selectedVM.fetched_at).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Service: {selectedVM.service}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    VM Actions & Cost
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Button variant="contained" color="success">Start</Button>
                    <Button variant="contained" color="warning">Stop</Button>
                    <Button variant="contained" color="info">Restart</Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DateTimePicker
                        label="Schedule Start"
                        value={null}
                        onChange={() => {}}
                        renderInput={(params) => <TextField {...params} size="small" />}
                      />
                      <DateTimePicker
                        label="Schedule Stop"
                        value={null}
                        onChange={() => {}}
                        renderInput={(params) => <TextField {...params} size="small" />}
                      />
                    </LocalizationProvider>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TextField size="small" label="Add Tag" variant="outlined" />
                    <Button variant="outlined">Add</Button>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body1">
                      Cost: <b>$24.50/month</b>
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      Cost Saving Tip: This VM appears idle. You could save <b>$18.00/month</b> by stopping it.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

const sectionMeta = {
  vm: {
    label: 'Virtual Machines',
    icon: <Computer sx={{ fontSize: 32, color: '#1976d2', mr: 1 }} />,
    color: '#e3f2fd',
  },
  network: {
    label: 'Networks',
    icon: <NetworkCheck sx={{ fontSize: 32, color: '#43a047', mr: 1 }} />,
    color: '#e8f5e9',
  },
  disk: {
    label: 'Disks',
    icon: <Storage sx={{ fontSize: 32, color: '#fbc02d', mr: 1 }} />,
    color: '#fffde7',
  },
};

const Inventory = ({ cloud, onBack, sidebarCategory }) => {
  const [resources, setResources] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    region: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });
  const [regionInput, setRegionInput] = useState('');

  // Debounced filter update for region
  const debouncedSetRegion = useCallback(
    debounce((value) => {
      setFilters(prev => ({ ...prev, region: value }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500),
    []
  );

  const handleRegionInputChange = (e) => {
    setRegionInput(e.target.value);
    debouncedSetRegion(e.target.value);
  };

  useEffect(() => {
    fetchData();
  }, [cloud, filters, pagination.page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch resources
      const resourcesResponse = await inventoryAPI.getResources(cloud, {
        ...filters,
        limit: pagination.limit,
        offset: (pagination.page - 1) * pagination.limit,
      });

      setResources(resourcesResponse.data.resources);
      setPagination(prev => ({
        ...prev,
        total: resourcesResponse.data.pagination.total,
      }));

      // Fetch stats
      const statsResponse = await inventoryAPI.getStats(cloud);
      setStats(statsResponse.data);
    } catch (err) {
      setError('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'vm':
        return <Cloud sx={{ fontSize: 20 }} />;
      case 'disk':
        return <Storage sx={{ fontSize: 20 }} />;
      case 'network':
        return <NetworkCheck sx={{ fontSize: 20 }} />;
      default:
        return <Cloud sx={{ fontSize: 20 }} />;
    }
  };

  const formatTags = (tags) => {
    if (!tags || typeof tags === 'string') {
      try {
        tags = JSON.parse(tags);
      } catch {
        return {};
      }
    }
    return JSON.stringify(tags, null, 2);
  };

  const getCloudName = (cloudId) => {
    const cloudNames = {
      aws: 'AWS',
      gcp: 'GCP',
      azure: 'Azure',
    };
    return cloudNames[cloudId] || cloudId.toUpperCase();
  };

  // Filter resources by sidebarCategory if set
  const filteredResources = sidebarCategory
    ? resources.filter(r => r.category === sidebarCategory)
    : resources;

  if (loading && resources.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f7fafd', minHeight: '100vh' }}>
      <AppBar position="static" elevation={0} sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {getCloudName(cloud)} Inventory
          </Typography>
          <IconButton color="inherit" onClick={fetchData}>
            <Refresh />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Section Heading */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {sectionMeta[sidebarCategory]?.icon}
          <Typography variant="h4" fontWeight={700} sx={{ color: '#222' }}>
            {sectionMeta[sidebarCategory]?.label || 'All Resources'}
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map((stat) => (
            <Grid item xs={12} sm={6} md={3} key={stat.category}>
              <Card sx={{
                bgcolor: sectionMeta[stat.category]?.color || '#fff',
                boxShadow: 2,
                borderRadius: 2,
                minHeight: 120,
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {sectionMeta[stat.category]?.icon}
                    <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                      {stat.category.toUpperCase()}
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" fontWeight={700}>
                    {stat.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.regions} regions • Last updated: {new Date(stat.last_updated).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Filters (only Region remains) */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#f1f8e9', borderRadius: 2, boxShadow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterList sx={{ mr: 1, color: '#43a047' }} />
            <Typography variant="h6" fontWeight={600}>Filters</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Region"
                value={regionInput}
                onChange={handleRegionInputChange}
                placeholder="e.g., us-east-1"
                size="small"
                sx={{ bgcolor: '#fff', borderRadius: 1 }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Resources Display */}
        {sidebarCategory === 'vm' ? (
          // VM Grid View
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}>
              Virtual Machines ({filteredResources.length})
            </Typography>
            {filteredResources.length > 0 ? (
              <VMGrid vms={filteredResources} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No virtual machines found.
              </Typography>
            )}
          </Box>
        ) : sidebarCategory === 'disk' || sidebarCategory === 'network' ? (
          // Table View for Disk or Network
          <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 2, borderRadius: 2, bgcolor: '#fff' }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell>Resource ID</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell>Last Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredResources.map((resource) => (
                    <TableRow key={resource.resource_id} hover sx={{ transition: 'background 0.2s', '&:hover': { bgcolor: '#e3f2fd' } }}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {resource.resource_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getCategoryIcon(resource.category)}
                          <Chip
                            label={resource.category.toUpperCase()}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>{resource.service}</TableCell>
                      <TableCell>{resource.name}</TableCell>
                      <TableCell>{resource.region}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={formatTags(resource.tags)}
                        >
                          {formatTags(resource.tags)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(resource.fetched_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          // Show all resources if no sidebarCategory (fallback)
          <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 2, borderRadius: 2, bgcolor: '#fff' }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell>Resource ID</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell>Last Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resources.map((resource) => (
                    <TableRow key={resource.resource_id} hover sx={{ transition: 'background 0.2s', '&:hover': { bgcolor: '#e3f2fd' } }}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {resource.resource_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getCategoryIcon(resource.category)}
                          <Chip
                            label={resource.category.toUpperCase()}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>{resource.service}</TableCell>
                      <TableCell>{resource.name}</TableCell>
                      <TableCell>{resource.region}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={formatTags(resource.tags)}
                        >
                          {formatTags(resource.tags)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(resource.fetched_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={Math.ceil(pagination.total / pagination.limit)}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          Showing {filteredResources.length} of {pagination.total} resources
        </Typography>
      </Container>
    </Box>
  );
};

export default Inventory; 